import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DokumentService } from '../dokument.service';
import { DatePipe } from '@angular/common';
import { CprNummerPipe } from '@core/pipes/cprnummer.pipe';
import { Dokument } from '@features/dokumenter/dokument.model';
import { SimpleApi } from '@core/api/simple.api';
import { DokumentSigneringStatus } from '../dokument-signering-status.model';
import { DokumentSigneringPart } from '../dokument-signering-part.model';
import { SigneringPart } from '../signering-part';
import { DokumentSignering } from '../dokument-signering.model';
import { DokumentApi } from '../dokument.api';
import { LetLoadingIndicatorComponent } from '@shared/let-loading-indicator/let-loading-indicator.component';

@Component({
  selector: 'app-dokument-detail',
  imports: [
    ScrollPanelModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePipe,
    CprNummerPipe,
    LetLoadingIndicatorComponent,
  ],
  providers: [DatePipe, CprNummerPipe],
  templateUrl: './dokument-detail.component.html',
  styleUrl: './dokument-detail.component.css',
})
export class DokumentDetailComponent {
  datePipe = inject(DatePipe);
  cprNummerPipe = inject(CprNummerPipe);
  dokService = inject(DokumentService);
  dokumentApi = inject(DokumentApi);
  simpleApi = inject(SimpleApi);
  bilag = signal<Dokument[]>([]);

  constructor() {
    effect(async () => {
      const dokument = this.dokService.selectedDokument();
      untracked(async () => {
        if (dokument?.dokumentSignering) {
          const response = await this.dokumentApi.getList({
            filter: JSON.stringify({
              dokumentSigneringId: dokument.dokumentSignering.id,
            }),
          });
          this.bilag.set(response.data);
        }
      });
    });
  }

  async onClickShow() {
    await this.dokService.hentDokument(
      this.dokService.selectedDokument()!,
      false
    );
  }

  async onClickDownload() {
    await this.dokService.hentDokument(
      this.dokService.selectedDokument()!,
      true
    );
  }

  async getDokumentFile(dokument: Dokument, download = false) {
    const blob = await this.simpleApi.getBlob(
      `/api/admin/dokumenter/${dokument.id}/download`,
      {
        dowload: download,
      }
    );
    const objectUrl = window.URL.createObjectURL(blob);
    if (download) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute(
        'download',
        dokument.dokumentType?.beskrivelse + '.pdf'
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      window.open(objectUrl);
    }
  }

  signeringHistorik = computed(() => {
    const historik =
      this.dokService.selectedDokument()?.dokumentSignering
        ?.dokumentSigneringStatusHistorik || [];
    historik.sort((a, b) =>
      a.signeringsStatusTidspunkt > b.signeringsStatusTidspunkt ? 1 : -1
    );
    const historikTekster: string[] = [];
    let previous = undefined;
    for (const h of historik) {
      const tekst = this.getHistorikTekst(h, previous);
      historikTekster.push(tekst);
      previous = h;
    }
    return historikTekster;
  });

  getHistorikTekst(
    status: DokumentSigneringStatus,
    previous?: DokumentSigneringStatus
  ) {
    let signeringStatus = '';
    if (status.signeringsStatus === 'AfventerUnderskrift') {
      if (status.signeringsMetode !== previous?.signeringsMetode) {
        signeringStatus +=
          'Opdatering af underskriftmetode til ' +
          status.signeringsMetode +
          ' den ' +
          this.getSigneringsStatusTidspunkt(status);
        signeringStatus += '. Afventer underskrift';
      }
    } else if (status.signeringsStatus === 'SendtTilSignering') {
      if (status.signeringsMetode !== 'Ukendt') {
        signeringStatus +=
          'Opdatering af signeringsstatus den ' +
          this.getSigneringsStatusTidspunkt(status);
        signeringStatus += ' af ' + this.getSigneringsStatusBruger(status);
        signeringStatus += '. Signeringsmetode ' + status.signeringsMetode;
      } else {
        signeringStatus +=
          status.signeringsStatus +
          ' den ' +
          this.getSigneringsStatusTidspunkt(status);
        signeringStatus += ' af ' + this.getSigneringsStatusBruger(status);
      }
    } else if (status.signeringsStatus === 'Tilbagekaldt') {
      signeringStatus +=
        status.signeringsStatus +
        ' den ' +
        this.getSigneringsStatusTidspunkt(status);
      signeringStatus += ' af ' + this.getSigneringsStatusBruger(status);
      signeringStatus += '. Signeringsmetode ' + status.signeringsMetode;
    } else if (status.signeringsStatus === 'Afvist') {
      signeringStatus +=
        status.signeringsStatus +
        ' den ' +
        this.getSigneringsStatusTidspunkt(status);
      signeringStatus += '. Årsag: ' + status.signeringsStatusDetaljer;
    } else if (status.signeringsStatus === 'Underskrevet') {
      if (status.signeringsMetode === 'Digitalt') {
        signeringStatus += 'Modtaget som digitalt underskrevet ';
        signeringStatus += ' den ' + this.getSigneringsStatusTidspunkt(status);
      } else {
        signeringStatus += status.signeringsStatus;
        signeringStatus += ' ' + status.signeringsMetode.toLowerCase();
        signeringStatus += ' den ' + this.getSigneringsStatusTidspunkt(status);
      }
    }

    return signeringStatus;
  }

  getSigneringsStatusTidspunkt(
    dokumentSigneringStatus: DokumentSigneringStatus
  ) {
    let signeringsStatusTidspunkt = this.datePipe.transform(
      dokumentSigneringStatus.signeringsStatusTidspunkt,
      'd/M-yyyy'
    );

    if (
      dokumentSigneringStatus.signeringsMetode != 'Fysisk' &&
      dokumentSigneringStatus.signeringsMetode != 'E_Engagement'
    ) {
      signeringsStatusTidspunkt +=
        ' kl. ' +
        this.datePipe.transform(
          dokumentSigneringStatus.signeringsStatusTidspunkt,
          'HH:mm'
        );
    }
    return signeringsStatusTidspunkt;
  }

  getSigneringsStatusBruger(dokumentSigneringStatus: DokumentSigneringStatus) {
    let signeringsStatusBruger = '';
    if (dokumentSigneringStatus.signeringsStatusBrugerType === 'Kunde') {
      signeringsStatusBruger += ' kunden ';
    }
    if (dokumentSigneringStatus.signeringsStatusBrugerType === 'Raadgiver') {
      signeringsStatusBruger +=
        ' rådgiver (' + dokumentSigneringStatus.signeringsStatusAf + ') ';
    }
    return signeringsStatusBruger;
  }

  getUnderskriftStatus(dokumentSigneringPart: DokumentSigneringPart) {
    let underskriftsstatus = '';

    if (this.dokService.selectedDokument()?.signeringStatus) {
      if (dokumentSigneringPart.signeringsTidspunkt != null) {
        underskriftsstatus += 'Underskrevet af ';
        if (dokumentSigneringPart.signeringspart?.signeringsRolle != null) {
          underskriftsstatus +=
            dokumentSigneringPart.signeringspart?.signeringsRolle.toLowerCase() +
            ', ';
        }
        underskriftsstatus +=
          this.getNavnOgNummer(dokumentSigneringPart.signeringspart) +
          ' den ' +
          this.datePipe.transform(
            dokumentSigneringPart.signeringsTidspunkt,
            'd/M-yyyy'
          );

        const aktuelSigneringsStatus =
          this.dokService.selectedDokument()?.signeringStatus;
        if (
          aktuelSigneringsStatus?.signeringsMetode !== 'Fysisk' &&
          aktuelSigneringsStatus?.signeringsMetode !== 'E_Engagement'
        ) {
          underskriftsstatus +=
            ' kl. ' +
            this.datePipe.transform(
              dokumentSigneringPart.signeringsTidspunkt,
              'HH:mm'
            );
        }
      } else {
        underskriftsstatus += 'Mangler underskrift af ';
        if (dokumentSigneringPart.signeringspart?.signeringsRolle != null) {
          underskriftsstatus +=
            dokumentSigneringPart.signeringspart?.signeringsRolle.toLowerCase() +
            ', ';
        }
        underskriftsstatus += this.getNavnOgNummer(
          dokumentSigneringPart.signeringspart
        );
      }
    } else {
      underskriftsstatus += 'Skal underskrives af ';
      if (dokumentSigneringPart.signeringspart?.signeringsRolle != null) {
        underskriftsstatus +=
          dokumentSigneringPart.signeringspart?.signeringsRolle.toLowerCase() +
          ', ';
      }
      underskriftsstatus += this.getNavnOgNummer(
        dokumentSigneringPart.signeringspart
      );
    }

    return underskriftsstatus;
  }

  getNavnOgNummer(part?: SigneringPart) {
    const navn = part?.arbejdsgiverNavn || part?.personNavn || '';
    const nummer =
      part?.cvrNummer || this.cprNummerPipe.transform(part?.cprNummer) || '';
    return navn + ' (' + nummer + ')';
  }

  skalUnderskrives(dokumentSignering: DokumentSignering) {
    if (dokumentSignering.dokumentSigneringParter) {
      if (dokumentSignering.dokumentSigneringStatusHistorik) {
        const aktuelSigneringsStatus =
          this.dokService.selectedDokument()?.signeringStatus;
        if (aktuelSigneringsStatus?.signeringsMetode === 'IngenUnderskrift') {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  async clickBilag(dokument: Dokument) {
    await this.dokService.hentDokument(dokument, false);
  }
}
